/**
 * Componente Loading reutilizável para indicar estados de carregamento
 * Usado em dashboards, formulários, operações assíncronas, etc.
 */

import React from 'react'
import { cn } from '../utils/cn'

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg' | 'xl'
  variant?: 'spinner' | 'dots' | 'pulse' | 'skeleton'
  text?: string
  fullScreen?: boolean
  overlay?: boolean
  className?: string
}

interface SkeletonProps {
  className?: string
  lines?: number
  avatar?: boolean
}

const Loading: React.FC<LoadingProps> & {
  Skeleton: React.FC<SkeletonProps>
} = ({
  size = 'md',
  variant = 'spinner',
  text,
  fullScreen = false,
  overlay = false,
  className
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  }

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  }

  const renderSpinner = () => (
    <svg
      className={cn(
        'animate-spin text-blue-600',
        sizeClasses[size]
      )}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )

  const renderDots = () => (
    <div className="flex space-x-1">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            'bg-blue-600 rounded-full animate-pulse',
            {
              'w-2 h-2': size === 'sm',
              'w-3 h-3': size === 'md',
              'w-4 h-4': size === 'lg',
              'w-5 h-5': size === 'xl'
            }
          )}
          style={{
            animationDelay: `${i * 0.2}s`,
            animationDuration: '1.4s'
          }}
        />
      ))}
    </div>
  )

  const renderPulse = () => (
    <div
      className={cn(
        'bg-blue-600 rounded-full animate-pulse',
        sizeClasses[size]
      )}
    />
  )

  const renderSkeleton = () => (
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 rounded w-1/2"></div>
      <div className="h-4 bg-gray-300 rounded w-5/6"></div>
    </div>
  )

  const renderVariant = () => {
    switch (variant) {
      case 'dots':
        return renderDots()
      case 'pulse':
        return renderPulse()
      case 'skeleton':
        return renderSkeleton()
      default:
        return renderSpinner()
    }
  }

  const content = (
    <div
      className={cn(
        'flex flex-col items-center justify-center space-y-2',
        {
          'fixed inset-0 z-50 bg-white': fullScreen,
          'absolute inset-0 bg-white bg-opacity-75 z-10': overlay && !fullScreen
        },
        className
      )}
    >
      {renderVariant()}
      {text && (
        <p className={cn(
          'text-gray-600 font-medium',
          textSizeClasses[size]
        )}>
          {text}
        </p>
      )}
    </div>
  )

  if (fullScreen || overlay) {
    return content
  }

  return (
    <div className={cn('flex items-center justify-center p-4', className)}>
      <div className="flex flex-col items-center space-y-2">
        {renderVariant()}
        {text && (
          <p className={cn(
            'text-gray-600 font-medium',
            textSizeClasses[size]
          )}>
            {text}
          </p>
        )}
      </div>
    </div>
  )
}

const Skeleton: React.FC<SkeletonProps> = ({
  className,
  lines = 3,
  avatar = false
}) => {
  return (
    <div className={cn('animate-pulse', className)}>
      {avatar && (
        <div className="flex items-center space-x-4 mb-4">
          <div className="rounded-full bg-gray-300 h-12 w-12"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded w-1/4"></div>
            <div className="h-3 bg-gray-300 rounded w-1/3"></div>
          </div>
        </div>
      )}
      
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, index) => {
          const widths = ['w-full', 'w-5/6', 'w-4/5', 'w-3/4', 'w-2/3']
          const randomWidth = widths[index % widths.length]
          
          return (
            <div
              key={index}
              className={cn('h-4 bg-gray-300 rounded', randomWidth)}
            />
          )
        })}
      </div>
    </div>
  )
}

Loading.Skeleton = Skeleton

export default Loading